/* AWA Evaporative installation card (v4). Same structure and states as the split-system card.
   Supply only | Supply + Installation -> 1 location (postcode, locked to the page Postcode checker)
   -> 2 installation service -> 3 controller (Seeley family) -> summary -> adds unit + installation (+ controller).
   If a service product doesn't resolve server-side, it is loaded from /products/<handle>.js so the options never render empty. */
(() => {
  if (customElements.get('awa-evap-product-card')) return;

  const money = (cents) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format((Number(cents) || 0) / 100);

  class AWAEvapProductCard extends HTMLElement {
    connectedCallback() {
      if (this.ready) return; this.ready = true;
      try { this.cfg = JSON.parse(this.querySelector('[data-awa-evap-data]')?.textContent || '{}'); } catch (_) { this.cfg = {}; }
      this.copy = this.cfg.copy || {};
      this.section = this.closest('.shopify-section') || document.body;
      this.tabs = [...this.querySelectorAll('[data-awa-evap-tab]')];
      this.panels = [...this.querySelectorAll('[data-awa-evap-panel]')];
      this.pcInput = this.querySelector('[data-awa-evap-postcode]');
      this.pcForm = this.querySelector('[data-awa-evap-postcode-form]');
      this.pcError = this.querySelector('[data-awa-evap-pc-error]');
      this.pcBox = this.querySelector('[data-awa-evap-pc-result]');
      this.addBtn = this.querySelector('[data-awa-evap-add]');
      this.msg = this.querySelector('[data-awa-evap-msg]');
      this.quote = this.querySelector('[data-awa-product-quote-open]');
      this.outQuote = this.querySelector('[data-awa-evap-out-quote]');
      this.postcodeOk = false;
      this.coverage = 'neutral';
      this.pcValue = '';

      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-awa-evap-tab]')) this.setMode(e.target.value);
        if (e.target.matches('[data-awa-evap-type], [data-awa-evap-controller]')) this.render();
      });
      this.pcForm?.addEventListener('submit', (e) => { e.preventDefault(); this.checkPostcode(); });
      this.pcInput?.addEventListener('input', () => { if (this.postcodeOk) { this.postcodeOk = false; this.render(); } });
      this.addBtn?.addEventListener('click', () => this.addToCart());
      this.outQuote?.addEventListener('click', () => this.quote?.click());
      // keep the summary in sync with the main product form (variant + quantity)
      this.section.addEventListener('change', (e) => { if (!this.contains(e.target)) setTimeout(() => this.render(), 50); });
      this.section.addEventListener('input', (e) => { if (!this.contains(e.target)) setTimeout(() => this.render(), 50); });
      this.hydrated = false;
      this.setMode('supply');
      this.hydrate();
    }

    // Load any service product Liquid couldn't resolve, so the options are never empty.
    async hydrate() {
      const keys = ['swap', 'dropper', 'controller'];
      await Promise.all(keys.map(async (k) => {
        const c = this.cfg[k];
        if (!c || c.available || !c.handle) return;
        try {
          const r = await fetch('/products/' + encodeURIComponent(c.handle) + '.js', { headers: { Accept: 'application/json' } });
          if (!r.ok) return;
          const p = await r.json();
          const v = (p.variants || []).find((x) => x.available);
          if (!v) return;
          Object.assign(c, { id: v.id, price: v.price, title: c.title || p.title, available: true });
          const card = this.querySelector('[data-awa-evap-option="' + k + '"]');
          if (card) {
            card.classList.remove('is-unavailable');
            const inp = card.querySelector('input'); if (inp) inp.disabled = false;
            const pr = card.querySelector('[data-awa-evap-option-price]'); if (pr) pr.textContent = (k === 'controller' ? '+' : '') + money(v.price);
          }
          if (k === 'controller') { const n = this.querySelector('[data-awa-evap-sum-ctrl-name]'); if (n && !n.textContent.trim()) n.textContent = c.title; }
        } catch (_) {}
      }));
      // Anything still unresolved shows as unavailable, like the split card.
      keys.forEach((k) => {
        const c = this.cfg[k]; const card = this.querySelector('[data-awa-evap-option="' + k + '"]');
        if (card && !(c && c.available)) { const pr = card.querySelector('[data-awa-evap-option-price]'); if (pr) pr.textContent = 'Unavailable'; }
      });
      this.hydrated = true;
      if (!this.querySelector('[data-awa-evap-type]:checked')) {
        const first = this.querySelector('[data-awa-evap-type]:not(:disabled)');
        if (first) first.checked = true;
      }
      this.render();
    }

    mainForm() {
      const forms = [...this.section.querySelectorAll('form[action*="/cart/add"]')].filter((f) => !this.contains(f) && f.querySelector('[name="id"]'));
      return forms[0] || null;
    }
    unit() {
      const f = this.mainForm();
      const id = f?.querySelector('[name="id"]')?.value || this.cfg.unit?.id;
      const qtyEl = this.section.querySelector('input[name="quantity"]:not([form]), input[name="quantity"]');
      const qty = Math.max(1, parseInt(qtyEl?.value, 10) || 1);
      const price = (this.cfg.variants || {})[String(id)] ?? this.cfg.unit?.price ?? 0;
      return { id: Number(id), qty, price };
    }
    choice(sel) { return this.querySelector(sel + ':checked')?.value || ''; }

    setMode(mode) {
      this.mode = mode;
      this.tabs.forEach((t) => { t.checked = t.value === mode; });
      this.panels.forEach((p) => { p.hidden = p.dataset.awaEvapPanel !== mode; });
      const install = mode === 'install';
      this.tabs.forEach((t) => { if (t.hasAttribute('aria-controls')) t.setAttribute('aria-expanded', String(install)); });
      this.section.toggleAttribute('data-awa-evap-install-mode', install);
      document.body.toggleAttribute('data-awa-evap-install-mode', install);
      if (this.quote) this.quote.dataset.quotePreselect = install ? 'Supply + Installation' : 'Supply Only';
      this.render();
    }

    checkPostcode() {
      const pc = String(this.pcInput?.value || '').trim();
      try { this.state = window.AWADeliveryChecker?.stateOf?.(pc) || ''; } catch (_) { this.state = ''; }
      const engine = window.AWAInstallationCoverage;
      this.postcodeOk = false; this.pcValue = pc;
      if (!/^[0-9]{4}$/.test(pc)) return this.showResult('invalid');
      if (!this.cfg.rules || !engine?.parseRules || !engine?.evaluate) { this.postcodeOk = true; return this.showResult('available', pc); }
      const parsed = engine.parseRules(this.cfg.rules);
      const r = parsed.valid ? engine.evaluate({ enabled: true, parsed, rules: this.cfg.rules }, pc) : { available: null };
      if (r.available === false) return this.showResult('unavailable', pc);
      this.postcodeOk = true;
      this.showResult('available', pc);
    }

    // Same result box as the split card's installation checker.
    showResult(state, pc) {
      this.coverage = state === 'invalid' ? 'neutral' : state;
      if (state === 'invalid') {
        if (this.pcError) { this.pcError.textContent = this.copy.invalid || 'Enter a valid 4-digit Australian postcode.'; this.pcError.hidden = false; }
        if (this.pcBox) this.pcBox.hidden = true;
        return this.render();
      }
      if (this.pcError) this.pcError.hidden = true;
      if (this.pcBox) {
        const ok = state === 'available';
        this.pcBox.hidden = false;
        this.pcBox.classList.toggle('is-available', ok);
        this.pcBox.classList.toggle('is-unavailable', !ok);
        const icon = this.pcBox.querySelector('[data-awa-evap-pc-icon]'); if (icon) icon.textContent = ok ? '\u2713' : '\u00d7';
        const st = this.pcBox.querySelector('[data-awa-evap-pc-status]'); if (st) st.textContent = ok ? this.copy.availableHeading : this.copy.unavailableHeading;
        const m = this.pcBox.querySelector('[data-awa-evap-pc-message]'); if (m) m.textContent = String((ok ? this.copy.availableMessage : this.copy.unavailableMessage) || '').replace('[POSTCODE]', pc);
      }
      this.render();
    }

    lines() {
      const u = this.unit();
      const type = this.choice('[data-awa-evap-type]');
      const svc = type === 'dropper' ? this.cfg.dropper : type === 'swap' ? this.cfg.swap : null;
      const ctrl = this.choice('[data-awa-evap-controller]') === 'upgrade' && this.cfg.controller?.available ? this.cfg.controller : null;
      return { u, svc, ctrl, type };
    }

    render() {
      const install = this.mode === 'install';
      this.querySelectorAll('[data-awa-evap-lockable]').forEach((s) => s.classList.toggle('is-locked', !this.postcodeOk));
      if (this.outQuote) this.outQuote.hidden = !(install && this.coverage === 'unavailable');
      const anyService = !!(this.cfg.swap?.available || this.cfg.dropper?.available);
      const grid = this.querySelector('[data-awa-evap-service-grid]');
      const none = this.querySelector('[data-awa-evap-no-service]');
      const settled = this.hydrated !== false;
      if (none) none.hidden = anyService || !settled;
      if (grid) grid.hidden = !anyService && settled;
      if (!install) return;
      const { u, svc, ctrl } = this.lines();
      const set = (sel, v) => { const el = this.querySelector(sel); if (el) el.textContent = v; };
      const q = u.qty > 1 ? u.qty + ' \u00d7 ' : '';
      set('[data-awa-evap-sum-unit]', q + money(u.price));
      set('[data-awa-evap-sum-install]', svc?.available ? q + money(svc.price) : 'Quote');
      set('[data-awa-evap-sum-install-name]', svc?.title || 'Installation');
      const ctrlRow = this.querySelector('[data-awa-evap-sum-ctrl-row]');
      if (ctrlRow) ctrlRow.hidden = !ctrl;
      if (ctrl) set('[data-awa-evap-sum-ctrl]', q + money(ctrl.price));
      const total = (u.price + (svc?.available ? svc.price : 0) + (ctrl ? ctrl.price : 0)) * u.qty;
      set('[data-awa-evap-sum-total]', money(total));
      const ok = this.postcodeOk && svc?.available && u.id;
      if (this.addBtn) this.addBtn.disabled = !ok;
      if (this.msg && !this.msg.classList.contains('is-error')) {
        this.msg.textContent = !this.postcodeOk ? (this.copy.prompt || '') : (!anyService && settled ? (this.copy.noService || '') : '');
      }
      if (this.quote) {
        this.quote.dataset.installationPostcode = this.pcValue;
        this.quote.dataset.evapInstallationOption = svc?.title || '';
        this.quote.dataset.evapController = ctrl ? (ctrl.title + ' upgrade') : (this.cfg.standardController || 'Standard controller');
      }
    }

    async addToCart() {
      const { u, svc, ctrl, type } = this.lines();
      if (!this.postcodeOk || !svc?.available || !u.id) return;
      const forTitle = this.cfg.productTitle || '';
      const items = [
        { id: u.id, quantity: u.qty, properties: { 'Installation': (type === 'dropper' ? 'Swap with new dropper & collars' : 'Swap over') + ' \u2013 postcode ' + this.pcValue } },
        { id: svc.id, quantity: u.qty, properties: { 'For': forTitle, 'Installation postcode': this.pcValue, 'Installation state': this.state || '' } }
      ];
      if (ctrl) items.push({ id: ctrl.id, quantity: u.qty, properties: { 'For': forTitle, '_Controller upgrade': 'yes' } });
      this.addBtn.disabled = true; this.addBtn.classList.add('loading');
      if (this.msg) { this.msg.classList.remove('is-error'); this.msg.textContent = ''; }
      try {
        const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart');
        window.location.href = '/cart';
      } catch (err) {
        if (this.msg) { this.msg.classList.add('is-error'); this.msg.textContent = (this.copy.error || 'This package could not be added.') + ' ' + err.message + '. Please call 1300 440 550 or request a quote.'; }
        this.addBtn.disabled = false; this.addBtn.classList.remove('loading');
      }
    }
  }
  customElements.define('awa-evap-product-card', AWAEvapProductCard);
})();
